import axios from 'axios';
import { getAccountsNeedingSync, markSynced as markAccountSynced, listAccounts, updateAccount, removeAccount } from './db/repositories/accounts.ts';
import { getCategoriesNeedingSync, markSynced as markCategorySynced, listCategories, updateCategory, removeCategory } from './db/repositories/categories.ts';
import { getTransactionsNeedingSync, markSynced as markTransactionSynced, listTransactions, updateTransaction, removeTransaction } from './db/repositories/transactions.ts';
import { getTasksNeedingSync, markSynced as markTaskSynced, listTasks, updateTask, removeTask } from './db/repositories/tasks.ts';
import { dbAdd } from './db/indexedDb.ts';
import { isBackendOnline } from '../utils/backendStatus.ts';
import { getApiBaseUrl } from '../utils/getApiUrl.ts';
import { notificationService } from './notificationService.ts';
import { isPWAInstalled } from '../utils/deviceUtils.ts';
import { processSyncQueue } from './syncQueue.ts';
import { createObjective, updateObjective, getObjectiveByServerId, markObjectiveSynced } from './db/repositories/okrObjectives.ts';
import { createKeyResult, updateKeyResult, getKeyResultByServerId, markKeyResultSynced } from './db/repositories/okrKeyResults.ts';
import { createIndicator, updateIndicator, getIndicatorByServerId, markIndicatorSynced } from './db/repositories/okrIndicators.ts';
import { createInvestmentPlan, updateInvestmentPlan, getInvestmentPlanByServerId, markInvestmentPlanSynced } from './db/repositories/investmentPlans.ts';
import { createInvestmentScenario, updateInvestmentScenario, getInvestmentScenarioByServerId, markInvestmentScenarioSynced } from './db/repositories/investmentScenarios.ts';
import { createInvestmentLedger, updateInvestmentLedger, getInvestmentLedgerByServerId, markInvestmentLedgerSynced } from './db/repositories/investmentLedgers.ts';
import { createInvestmentRecord, updateInvestmentRecord, getInvestmentRecordByServerId, markInvestmentRecordSynced } from './db/repositories/investmentRecords.ts';
import { createInvestmentGoal, updateInvestmentGoal, getInvestmentGoalByServerId, markInvestmentGoalSynced } from './db/repositories/investmentGoals.ts';
const API_BASE_URL = getApiBaseUrl();
let syncInFlight: Promise<{ success: boolean; reason?: string }> | null = null;

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function isOnline(): boolean {
  return navigator.onLine;
}

async function syncToServer() {
  if (!isOnline()) return { success: false, reason: 'offline' };
  
  // Verificar se backend está realmente acessível
  const backendOnline = await isBackendOnline();
  if (!backendOnline) return { success: false, reason: 'backend-offline' };
  
  const token = localStorage.getItem('token');
  if (!token) return { success: false, reason: 'no-auth' };

  try {
    const api = axios.create({
      baseURL: API_BASE_URL,
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
    });

    // Sync Accounts
    const accountsToSync = await getAccountsNeedingSync();
    for (const account of accountsToSync) {
      try {
        if (account.server_id) {
          // Update existing
          await api.put(`/api/v1/accounts/${account.server_id}`, {
            name: account.name,
            description: account.description,
            balance: account.balance,
            is_default: account.is_default ?? false,
            is_investment: account.is_investment ?? false
          });
        } else {
          // Create new
          const response = await api.post('/api/v1/accounts', {
            name: account.name,
            description: account.description,
            balance: account.balance,
            is_default: account.is_default ?? false,
            is_investment: account.is_investment ?? false
          });
          await markAccountSynced(account.id, response.data.id);
        }
      } catch (e: any) {
        console.error('Error syncing account:', e);
      }
    }

    // Sync Categories
    const categoriesToSync = await getCategoriesNeedingSync();
    for (const category of categoriesToSync) {
      try {
        if (category.server_id) {
          await api.put(`/api/v1/categories/${category.server_id}`, {
            name: category.name,
            description: category.description,
            type: category.type,
            icon: category.icon,
            icon_color: category.icon_color,
            is_default: category.is_default ?? false
          });
        } else {
          const response = await api.post('/api/v1/categories', {
            name: category.name,
            description: category.description,
            type: category.type,
            icon: category.icon,
            icon_color: category.icon_color,
            is_default: category.is_default ?? false
          });
          await markCategorySynced(category.id, response.data.id);
        }
      } catch (e: any) {
        console.error('Error syncing category:', e);
      }
    }

    // Sync Tasks
    const tasksToSync = await getTasksNeedingSync();
    for (const task of tasksToSync) {
      try {
        if (task.server_id) {
          await api.put(`/api/v1/tasks/${task.server_id}`, {
            title: task.title,
            description: task.description,
            due_date: task.due_date,
            is_urgent: task.is_urgent,
            is_important: task.is_important,
            status: task.status,
            transaction_id: task.transaction_id || null
          });
        } else {
          const response = await api.post('/api/v1/tasks', {
            title: task.title,
            description: task.description,
            due_date: task.due_date,
            is_urgent: task.is_urgent,
            is_important: task.is_important,
            status: task.status,
            transaction_id: task.transaction_id || null
          });
          await markTaskSynced(task.id, response.data.id);
        }
      } catch (e: any) {
        console.error('Error syncing task:', e);
      }
    }

    // Sync Transactions
    const transactionsToSync = await getTransactionsNeedingSync();
    for (const transaction of transactionsToSync) {
      try {
        if (transaction.server_id) {
          await api.put(`/api/v1/transactions/${transaction.server_id}`, {
            description: transaction.description,
            amount: transaction.amount,
            type: transaction.type,
            due_date: transaction.due_date,
            is_paid: transaction.is_paid,
            paid_date: transaction.paid_date,
            account_id: transaction.account_id,
            category_id: transaction.category_id
          });
        } else {
          const response = await api.post('/api/v1/transactions', {
            description: transaction.description,
            amount: transaction.amount,
            type: transaction.type,
            due_date: transaction.due_date,
            is_paid: transaction.is_paid,
            paid_date: transaction.paid_date,
            account_id: transaction.account_id,
            category_id: transaction.category_id
          });
          await markTransactionSynced(transaction.id, response.data.id);
        }
      } catch (e: any) {
        console.error('Error syncing transaction:', e);
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error('Sync to server error:', error);
    return { success: false, reason: error.message };
  }
}

async function syncFromServer() {
  if (!isOnline()) return { success: false, reason: 'offline' };
  
  // Verificar se backend está realmente acessível
  const backendOnline = await isBackendOnline();
  if (!backendOnline) return { success: false, reason: 'backend-offline' };
  
  const token = localStorage.getItem('token');
  if (!token) return { success: false, reason: 'no-auth' };

  try {
    const api = axios.create({
      baseURL: API_BASE_URL,
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
    });

    // Get server data
    const [serverAccounts, serverCategories, serverTransactions, serverTasks] = await Promise.all([
      api.get('/api/v1/accounts'),
      api.get('/api/v1/categories'),
      api.get('/api/v1/transactions'),
      api.get('/api/v1/tasks')
    ]);

    // Get local data
    const [localAccounts, localCategories, localTransactions, localTasks] = await Promise.all([
      listAccounts(),
      listCategories(),
      listTransactions(),
      listTasks()
    ]);

    // Sync Accounts: merge server data
    const serverAccountIds = new Set(serverAccounts.data.map((a: any) => a.id));
    
    for (const serverAccount of serverAccounts.data) {
      const local = localAccounts.find(a => a.server_id === serverAccount.id);
      if (!local) {
        // New from server - add locally
        await dbAdd('accounts', {
          name: serverAccount.name,
          description: serverAccount.description,
          balance: serverAccount.balance,
          is_default: serverAccount.is_default,
          is_investment: serverAccount.is_investment,
          server_id: serverAccount.id,
          last_synced_at: new Date().toISOString(),
          needs_sync: false,
          created_at: serverAccount.created_at || new Date().toISOString(),
          updated_at: serverAccount.updated_at
        } as any);
      } else if (local.needs_sync) {
        // Conflict: local has changes, compare timestamps
        const serverUpdated = new Date(serverAccount.updated_at || serverAccount.created_at);
        const localUpdated = new Date(local.updated_at || local.created_at);
        if (serverUpdated > localUpdated) {
          // Server wins
          await updateAccount(local.id, {
            name: serverAccount.name,
            description: serverAccount.description,
            balance: serverAccount.balance,
            is_default: serverAccount.is_default,
            is_investment: serverAccount.is_investment,
            server_id: serverAccount.id,
            last_synced_at: new Date().toISOString(),
            needs_sync: false,
            updated_at: serverAccount.updated_at
          });
        }
        // If local wins, keep local changes (will sync to server in syncToServer)
      } else {
        // Update local with server data (no local changes)
        await updateAccount(local.id, {
          name: serverAccount.name,
          description: serverAccount.description,
          balance: serverAccount.balance,
          is_default: serverAccount.is_default,
          is_investment: serverAccount.is_investment,
          server_id: serverAccount.id,
          last_synced_at: new Date().toISOString(),
          needs_sync: false,
          updated_at: serverAccount.updated_at
        });
      }
    }

    // Remove local accounts that were deleted on server
    // Only remove accounts that have a server_id (were synced before)
    for (const localAccount of localAccounts) {
      if (localAccount.server_id && !serverAccountIds.has(localAccount.server_id)) {
        console.log(`[Sync] Removendo conta local deletada no servidor: ${localAccount.name} (server_id: ${localAccount.server_id})`);
        await removeAccount(localAccount.id);
      }
    }

    // Sync Categories: similar logic
    const serverCategoryIds = new Set(serverCategories.data.map((c: any) => c.id));
    
    for (const serverCategory of serverCategories.data) {
      const local = localCategories.find(c => c.server_id === serverCategory.id);
      if (!local) {
        await dbAdd('categories', {
          name: serverCategory.name,
          description: serverCategory.description,
          type: serverCategory.type,
          icon: serverCategory.icon,
          icon_color: serverCategory.icon_color,
          is_default: serverCategory.is_default,
          server_id: serverCategory.id,
          last_synced_at: new Date().toISOString(),
          needs_sync: false,
          created_at: serverCategory.created_at || new Date().toISOString(),
          updated_at: serverCategory.updated_at
        } as any);
      } else if (local.needs_sync) {
        const serverUpdated = new Date(serverCategory.updated_at || serverCategory.created_at);
        const localUpdated = new Date(local.updated_at || local.created_at);
        if (serverUpdated > localUpdated) {
          await updateCategory(local.id, {
            name: serverCategory.name,
            description: serverCategory.description,
            type: serverCategory.type,
            icon: serverCategory.icon,
            icon_color: serverCategory.icon_color,
            is_default: serverCategory.is_default,
            server_id: serverCategory.id,
            last_synced_at: new Date().toISOString(),
            needs_sync: false,
            updated_at: serverCategory.updated_at
          });
        }
      } else {
        await updateCategory(local.id, {
          name: serverCategory.name,
          description: serverCategory.description,
          type: serverCategory.type,
          icon: serverCategory.icon,
          icon_color: serverCategory.icon_color,
          is_default: serverCategory.is_default,
          server_id: serverCategory.id,
          last_synced_at: new Date().toISOString(),
          needs_sync: false,
          updated_at: serverCategory.updated_at
        });
      }
    }

    // Remove local categories that were deleted on server
    // Only remove categories that have a server_id (were synced before)
    for (const localCategory of localCategories) {
      if (localCategory.server_id && !serverCategoryIds.has(localCategory.server_id)) {
        console.log(`[Sync] Removendo categoria local deletada no servidor: ${localCategory.name} (server_id: ${localCategory.server_id})`);
        await removeCategory(localCategory.id);
      }
    }

    // Sync Transactions: similar logic
    const serverTransactionIds = new Set(serverTransactions.data.map((t: any) => t.id));
    
    for (const serverTransaction of serverTransactions.data) {
      const local = localTransactions.find(t => t.server_id === serverTransaction.id);
      if (!local) {
        await dbAdd('transactions', {
          description: serverTransaction.description,
          amount: serverTransaction.amount,
          type: serverTransaction.type,
          due_date: serverTransaction.due_date,
          paid_date: serverTransaction.paid_date,
          is_paid: serverTransaction.is_paid,
          account_id: serverTransaction.account_id,
          destination_account_id: serverTransaction.destination_account_id,
          category_id: serverTransaction.category_id,
          server_id: serverTransaction.id,
          last_synced_at: new Date().toISOString(),
          needs_sync: false,
          created_at: serverTransaction.created_at || new Date().toISOString(),
          updated_at: serverTransaction.updated_at
        } as any);
      } else if (local.needs_sync) {
        const serverUpdated = new Date(serverTransaction.updated_at || serverTransaction.created_at);
        const localUpdated = new Date(local.updated_at || local.created_at);
        if (serverUpdated > localUpdated) {
          await updateTransaction(local.id, {
            description: serverTransaction.description,
            amount: serverTransaction.amount,
            type: serverTransaction.type,
            due_date: serverTransaction.due_date,
            paid_date: serverTransaction.paid_date,
            is_paid: serverTransaction.is_paid,
            account_id: serverTransaction.account_id,
            destination_account_id: serverTransaction.destination_account_id,
            category_id: serverTransaction.category_id,
            server_id: serverTransaction.id,
            last_synced_at: new Date().toISOString(),
            needs_sync: false,
            updated_at: serverTransaction.updated_at
          });
        }
      } else {
        await updateTransaction(local.id, {
          description: serverTransaction.description,
          amount: serverTransaction.amount,
          type: serverTransaction.type,
          due_date: serverTransaction.due_date,
          paid_date: serverTransaction.paid_date,
          is_paid: serverTransaction.is_paid,
          account_id: serverTransaction.account_id,
          destination_account_id: serverTransaction.destination_account_id,
          category_id: serverTransaction.category_id,
          server_id: serverTransaction.id,
          last_synced_at: new Date().toISOString(),
          needs_sync: false,
          updated_at: serverTransaction.updated_at
        });
      }
    }

    // Remove local transactions that were deleted on server
    // Only remove transactions that have a server_id (were synced before)
    for (const localTransaction of localTransactions) {
      if (localTransaction.server_id && !serverTransactionIds.has(localTransaction.server_id)) {
        console.log(`[Sync] Removendo transação local deletada no servidor: ${localTransaction.description} (server_id: ${localTransaction.server_id})`);
        await removeTransaction(localTransaction.id);
      }
    }

    // Sync Tasks: similar logic
    const serverTaskIds = new Set(serverTasks.data.map((t: any) => t.id));
    
    for (const serverTask of serverTasks.data) {
      const local = localTasks.find(t => t.server_id === serverTask.id);
      if (!local) {
        await dbAdd('tasks', {
          title: serverTask.title,
          description: serverTask.description,
          due_date: serverTask.due_date,
          is_urgent: serverTask.is_urgent,
          is_important: serverTask.is_important,
          status: serverTask.status,
          transaction_id: serverTask.transaction_id,
          server_id: serverTask.id,
          last_synced_at: new Date().toISOString(),
          needs_sync: false,
          created_at: serverTask.created_at || new Date().toISOString(),
          updated_at: serverTask.updated_at
        } as any);
      } else if (local.needs_sync) {
        const serverUpdated = new Date(serverTask.updated_at || serverTask.created_at);
        const localUpdated = new Date(local.updated_at || local.created_at);
        if (serverUpdated > localUpdated) {
          await updateTask(local.id, {
            title: serverTask.title,
            description: serverTask.description,
            due_date: serverTask.due_date,
            is_urgent: serverTask.is_urgent,
            is_important: serverTask.is_important,
            status: serverTask.status,
            transaction_id: serverTask.transaction_id,
            server_id: serverTask.id,
            last_synced_at: new Date().toISOString(),
            needs_sync: false,
            updated_at: serverTask.updated_at
          });
        }
      } else {
        await updateTask(local.id, {
          title: serverTask.title,
          description: serverTask.description,
          due_date: serverTask.due_date,
          is_urgent: serverTask.is_urgent,
          is_important: serverTask.is_important,
          status: serverTask.status,
          transaction_id: serverTask.transaction_id,
          server_id: serverTask.id,
          last_synced_at: new Date().toISOString(),
          needs_sync: false,
          updated_at: serverTask.updated_at
        });
      }
    }

    // Remove local tasks that were deleted on server
    for (const localTask of localTasks) {
      if (localTask.server_id && !serverTaskIds.has(localTask.server_id)) {
        console.log(`[Sync] Removendo tarefa local deletada no servidor: ${localTask.title} (server_id: ${localTask.server_id})`);
        await removeTask(localTask.id);
      }
    }

    return { success: true };
  } catch (error: any) {
    console.error('Sync from server error:', error);
    return { success: false, reason: error.message };
  }
}

async function syncOkrsFromServer() {
  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
  });

  const serverObjectivesResponse = await api.get('/api/v1/objectives');
  const serverObjectives = serverObjectivesResponse.data || [];

  for (const serverObjective of serverObjectives) {
    const existing = await getObjectiveByServerId(serverObjective.id);
    if (!existing) {
      const created = await createObjective({
        title: serverObjective.title,
        description: serverObjective.description,
        period_type: serverObjective.period_type,
        period_start: serverObjective.period_start,
        period_end: serverObjective.period_end,
        status: serverObjective.status,
      });
      await markObjectiveSynced(created.id, serverObjective.id);
    } else if (!existing.needs_sync) {
      await updateObjective(existing.id, {
        title: serverObjective.title,
        description: serverObjective.description,
        period_type: serverObjective.period_type,
        period_start: serverObjective.period_start,
        period_end: serverObjective.period_end,
        status: serverObjective.status,
        needs_sync: false,
      });
    }

    const localObjective = (await getObjectiveByServerId(serverObjective.id))!;
    const detailResponse = await api.get(`/api/v1/objectives/${serverObjective.id}`);
    const detail = detailResponse.data;
    const serverKeyResults = detail?.key_results || [];

    for (const serverKr of serverKeyResults) {
      const localKr = await getKeyResultByServerId(serverKr.id);
      if (!localKr) {
        const created = await createKeyResult({
          objective_id: localObjective.id,
          title: serverKr.title,
          target_value: serverKr.target_value,
          unit: serverKr.unit,
          status: serverKr.status,
        });
        await markKeyResultSynced(created.id, serverKr.id);
      } else if (!localKr.needs_sync) {
        await updateKeyResult(localKr.id, {
          title: serverKr.title,
          target_value: serverKr.target_value,
          unit: serverKr.unit,
          status: serverKr.status,
          needs_sync: false,
        });
      }

      const localKeyResult = (await getKeyResultByServerId(serverKr.id))!;
      const indicatorsResponse = await api.get(`/api/v1/key-results/${serverKr.id}/indicators`);
      const serverIndicators = indicatorsResponse.data || [];
      for (const serverIndicator of serverIndicators) {
        const localIndicator = await getIndicatorByServerId(serverIndicator.id);
        if (!localIndicator) {
          const created = await createIndicator({
            key_result_id: localKeyResult.id,
            month: serverIndicator.month,
            value: serverIndicator.value,
          });
          await markIndicatorSynced(created.id, serverIndicator.id);
        } else if (!localIndicator.needs_sync) {
          await updateIndicator(localIndicator.id, {
            month: serverIndicator.month,
            value: serverIndicator.value,
            needs_sync: false,
          });
        }
      }
    }
  }
}

async function syncInvestmentsFromServer() {
  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
  });

  const plansResponse = await api.get('/api/v1/investment-plans');
  const serverPlans = plansResponse.data || [];
  for (const serverPlan of serverPlans) {
    const existing = await getInvestmentPlanByServerId(serverPlan.id);
    if (!existing) {
      const created = await createInvestmentPlan({
        name: serverPlan.name,
        current_age: serverPlan.current_age,
        withdrawal_start_age: serverPlan.withdrawal_start_age,
        life_expectancy: serverPlan.life_expectancy,
        initial_net_worth: serverPlan.initial_net_worth,
        monthly_contribution: serverPlan.monthly_contribution,
        withdrawal_amount: serverPlan.withdrawal_amount,
        expected_nominal_annual_return: serverPlan.expected_nominal_annual_return,
        expected_annual_inflation: serverPlan.expected_annual_inflation,
      });
      await markInvestmentPlanSynced(created.id, serverPlan.id);
    } else if (!existing.needs_sync) {
      await updateInvestmentPlan(existing.id, {
        name: serverPlan.name,
        current_age: serverPlan.current_age,
        withdrawal_start_age: serverPlan.withdrawal_start_age,
        life_expectancy: serverPlan.life_expectancy,
        initial_net_worth: serverPlan.initial_net_worth,
        monthly_contribution: serverPlan.monthly_contribution,
        withdrawal_amount: serverPlan.withdrawal_amount,
        expected_nominal_annual_return: serverPlan.expected_nominal_annual_return,
        expected_annual_inflation: serverPlan.expected_annual_inflation,
        needs_sync: false,
      });
    }
  }

  for (const serverPlan of serverPlans) {
    const localPlan = await getInvestmentPlanByServerId(serverPlan.id);
    if (!localPlan) continue;
    const scenariosResponse = await api.get(`/api/v1/investment-advisory/scenarios?plan_id=${serverPlan.id}`);
    const serverScenarios = scenariosResponse.data || [];
    for (const serverScenario of serverScenarios) {
      const localScenario = await getInvestmentScenarioByServerId(serverScenario.id);
      if (!localScenario) {
        const created = await createInvestmentScenario({
          plan_id: localPlan.id,
          name: serverScenario.name,
          return_adjustment: serverScenario.return_adjustment,
          inflation_adjustment: serverScenario.inflation_adjustment,
          retirement_age_adjustment: serverScenario.retirement_age_adjustment,
          notes: serverScenario.notes,
        });
        await markInvestmentScenarioSynced(created.id, serverScenario.id);
      } else if (!localScenario.needs_sync) {
        await updateInvestmentScenario(localScenario.id, {
          name: serverScenario.name,
          return_adjustment: serverScenario.return_adjustment,
          inflation_adjustment: serverScenario.inflation_adjustment,
          retirement_age_adjustment: serverScenario.retirement_age_adjustment,
          notes: serverScenario.notes,
          needs_sync: false,
        });
      }
    }
  }

  const ledgersResponse = await api.get('/api/v1/investment-tracking/ledgers');
  const serverLedgers = ledgersResponse.data || [];
  for (const serverLedger of serverLedgers) {
    const localLedger = await getInvestmentLedgerByServerId(serverLedger.id);
    if (!localLedger) {
      const created = await createInvestmentLedger({
        name: serverLedger.name,
        start_month: serverLedger.start_month,
        end_month: serverLedger.end_month,
        currency: serverLedger.currency,
      });
      await markInvestmentLedgerSynced(created.id, serverLedger.id);
    } else if (!localLedger.needs_sync) {
      await updateInvestmentLedger(localLedger.id, {
        name: serverLedger.name,
        start_month: serverLedger.start_month,
        end_month: serverLedger.end_month,
        currency: serverLedger.currency,
        needs_sync: false,
      });
    }
  }

  for (const serverLedger of serverLedgers) {
    const localLedger = await getInvestmentLedgerByServerId(serverLedger.id);
    if (!localLedger) continue;
    const recordsResponse = await api.get(`/api/v1/investment-tracking/records?ledger_id=${serverLedger.id}`);
    const serverRecords = recordsResponse.data || [];
    for (const serverRecord of serverRecords) {
      const localRecord = await getInvestmentRecordByServerId(serverRecord.id);
      if (!localRecord) {
        const created = await createInvestmentRecord({
          ledger_id: localLedger.id,
          reference_month: serverRecord.reference_month,
          opening_balance: serverRecord.opening_balance,
          contributions: serverRecord.contributions,
          withdrawals: serverRecord.withdrawals,
          closing_balance: serverRecord.closing_balance,
          data_source_note: serverRecord.data_source_note,
        });
        await markInvestmentRecordSynced(created.id, serverRecord.id);
      } else if (!localRecord.needs_sync) {
        await updateInvestmentRecord(localRecord.id, {
          reference_month: serverRecord.reference_month,
          opening_balance: serverRecord.opening_balance,
          contributions: serverRecord.contributions,
          withdrawals: serverRecord.withdrawals,
          closing_balance: serverRecord.closing_balance,
          data_source_note: serverRecord.data_source_note,
          needs_sync: false,
        });
      }
    }
  }

  const goalsResponse = await api.get('/api/v1/investment-advisory/goals');
  const serverGoals = goalsResponse.data || [];
  for (const serverGoal of serverGoals) {
    const localGoal = await getInvestmentGoalByServerId(serverGoal.id);
    let localPlanId: number | undefined;
    if (serverGoal.plan_id) {
      const plan = await getInvestmentPlanByServerId(serverGoal.plan_id);
      localPlanId = plan?.id;
    }
    if (!localGoal) {
      const created = await createInvestmentGoal({
        name: serverGoal.name,
        target_amount: serverGoal.target_amount,
        target_date: serverGoal.target_date,
        priority: serverGoal.priority,
        plan_id: localPlanId,
      });
      await markInvestmentGoalSynced(created.id, serverGoal.id);
    } else if (!localGoal.needs_sync) {
      await updateInvestmentGoal(localGoal.id, {
        name: serverGoal.name,
        target_amount: serverGoal.target_amount,
        target_date: serverGoal.target_date,
        priority: serverGoal.priority,
        plan_id: localPlanId,
        needs_sync: false,
      });
    }
  }
}

export async function performSync() {
  if (syncInFlight) {
    return syncInFlight;
  }

  syncInFlight = (async () => {
    if (!isOnline()) {
      // Verificar se é PWA instalada antes de permitir sincronização offline
      if (!isPWAInstalled()) {
        await notificationService.notifySyncError('Modo offline disponível apenas na versão instalada do app (PWA).');
        return { success: false, reason: 'offline-not-pwa' };
      }
      await notificationService.notifySyncError('Você está offline. Conecte-se à internet para sincronizar.');
      return { success: false, reason: 'offline' };
    }
    
    // Verificar se backend está realmente acessível
    const backendOnline = await isBackendOnline();
    if (!backendOnline) {
      await notificationService.notifySyncError('Backend não está acessível. Verifique sua conexão.');
      return { success: false, reason: 'Backend não está acessível' };
    }
    
    // Contar itens que precisam ser sincronizados ANTES da sincronização
    const accountsToSync = (await getAccountsNeedingSync()).length;
    const categoriesToSync = (await getCategoriesNeedingSync()).length;
    const transactionsToSync = (await getTransactionsNeedingSync()).length;
    const tasksToSync = (await getTasksNeedingSync()).length;
    const totalToSync = accountsToSync + categoriesToSync + transactionsToSync + tasksToSync;
    
    // First sync local changes to server
    const syncToResult = await syncToServer();
    if (!syncToResult.success) {
      await notificationService.notifySyncError(`Erro ao sincronizar: ${syncToResult.reason || 'Erro desconhecido'}`);
      return syncToResult;
    }

    // Process queued requests (recurring transactions, special endpoints)
    await processSyncQueue();
    
    // Then sync server changes to local
    const syncFromResult = await syncFromServer();
    if (!syncFromResult.success) {
      await notificationService.notifySyncError(`Erro ao sincronizar: ${syncFromResult.reason || 'Erro desconhecido'}`);
      return syncFromResult;
    }

    // Sync OKR and investment modules from server
    try {
      await syncOkrsFromServer();
      await syncInvestmentsFromServer();
    } catch (error: any) {
      await notificationService.notifySyncError(`Erro ao sincronizar: ${error?.message || 'Erro desconhecido'}`);
      return { success: false, reason: error?.message || 'Erro desconhecido' };
    }
    
    // Notificar sucesso com o número de itens que foram sincronizados
    await notificationService.notifySyncSuccess(totalToSync);
    
    // Salvar timestamp da última sincronização
    localStorage.setItem('last_sync_time', new Date().toISOString());
    
    return { success: true };
  })();

  try {
    return await syncInFlight;
  } finally {
    syncInFlight = null;
  }
}

export function setupAutoSync() {
  // Sync when coming back online
  window.addEventListener('online', () => {
    console.log('Back online, syncing...');
    performSync();
  });

  // Periodic sync every 5 minutes when online
  setInterval(() => {
    if (isOnline()) {
      performSync();
    }
  }, 5 * 60 * 1000);
}

